/**
 * Shared source type icon utilities for consistent icon rendering
 * across CitationPopover (SourceCard) and ReferencesSection (ReferenceCard).
 */

import { getFileIcon } from '@heidi/design-system/file-card';
import type { LucideIcon } from 'lucide-react';
import { Image as ImageIcon, Library, Paperclip } from 'lucide-react';

/**
 * Render props for source icons
 */
export const SOURCE_ICON_PROPS = {
  size: 14,
  className: 'shrink-0 text-text-tertiary',
} as const;

// ============================================================================
// File-Based Source Icons
// ============================================================================

/**
 * Get icon component for file-based source types.
 * Returns null for non-file sources (web_search).
 *
 * @param sourceType - The citation source_type ('user_document', 'image', 'attachment')
 * @param fileName - The file name (used for extension-based icon selection for user_document)
 */
export function getFileSourceIcon(
  sourceType?: string,
  fileName?: string
): LucideIcon | null {
  // Chat attachments use paperclip icon
  if (sourceType === 'attachment') {
    return Paperclip;
  }
  // Library documents use Library icon, or file-type icon if available
  if (sourceType === 'user_document') {
    if (fileName) {
      const extension = fileName.split('.').pop()?.toLowerCase();
      return getFileIcon(extension) || Library;
    }
    return Library;
  }
  if (sourceType === 'image') {
    return ImageIcon;
  }
  return null;
}

/**
 * Extract file type label from file name (e.g., "report.pdf" → "PDF").
 * Returns null if no valid extension found.
 */
export function getFileTypeLabel(fileName?: string): string | null {
  if (!fileName) return null;
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (!extension || extension === fileName.toLowerCase()) return null;
  return extension.toUpperCase();
}

// ============================================================================
// Source Type Labels (for display as text badges)
// ============================================================================

/**
 * Shortened labels for SourceWebsiteType values.
 * Used by both SourceCard (hover popover) and ReferenceCard (references table).
 * Succinct yet communicative for compact UI contexts.
 */
export const SOURCE_TYPE_LABELS: Record<string, string> = {
  'Medical News and Media': 'Medical News',
  'Medical Drug and Device Producer or Database': 'Medical Drug or Device Producer',
  'Clinical Laboratory & Diagnostic Service': 'Lab & Diagnostic Service',
  'Medical specialty society or college': 'Medical Society',
  'Medical Journal or Academic Publisher': 'Journal',
  'Clinical Education Website': 'Clinical Education',
  'Government Health Authority': 'Government Authority',
  'Clinical Practice Guideline Publisher': 'Guideline',
  'Academic or Research Institution': 'Academic Institution',
  'Healthcare Organization (hospital, clinic, etc.)': 'Health Organization',
  'Clinical Trial Registry or Database': 'Clinical Trial',
  'Patient Education and Support': 'Patient Education',
  'Consumer Health Information Website': 'Consumer Health',
  'Health Insurance and Financial Services': 'Health Insurance',
  'Alternative and Complementary Medicine': 'Alternative Medicine',
  'Non-Medical Commercial Website': 'Non-Medical',
  'Not a Medical Site': 'Non-Medical Site',
};

/**
 * Get shortened label for a source type, falling back to the original if not mapped.
 */
export function getSourceTypeLabel(type: string): string {
  return SOURCE_TYPE_LABELS[type] || type;
}
